/* CUSTOM: Collection page controller — client-side pagination of the custom collection grid (page templates
   rendered by custom-collection-product-list.liquid, URL parameter mymarini_page).
   Loaded on collection pages only from snippets/custom-global-head-assets.liquid. */
(() => {
  // --- CUSTOM SCRIPT START: Main collection pagination controller ---
  const MAIN_COLLECTION_PAGINATION_ROOT_SELECTOR =
    ".shopify-section--main-collection [data-custom-pagination-root]";
  let scheduledMainCollectionPaginationInit = 0;

  function buildMainCollectionPaginationUrl(page) {
    const url = new URL(window.location.href);

    ["section_id", "sections", "page", "mymarini_page"].forEach((paramName) => {
      url.searchParams.delete(paramName);
    });

    if (page > 1) {
      url.searchParams.set("mymarini_page", String(page));
    }

    return url;
  }

  function getRequestedMainCollectionPaginationPage() {
    const page = parseInt(new URLSearchParams(window.location.search).get("mymarini_page") || "", 10);

    return Number.isFinite(page) && page > 0 ? page : 1;
  }

  function buildMainCollectionPaginationLabel(template, page) {
    return template.replace("__PAGE__", String(page));
  }

  function applyMainCollectionPaginationNativeLayout(root) {
    root.classList.add("collection__main");
  }

  function updateMainCollectionPaginationNav(root, currentPage, totalPages) {
    const nav = root.querySelector("[data-custom-pagination-nav]");

    if (!(nav instanceof HTMLElement)) {
      return;
    }

    const labelTemplate = nav.dataset.customPaginationLabelTemplate || "Go to page __PAGE__";

    nav
      .querySelectorAll(
        "[data-custom-pagination-page]:not([data-custom-pagination-prev]):not([data-custom-pagination-next])"
      )
      .forEach((link) => {
        if (!(link instanceof HTMLAnchorElement)) {
          return;
        }

        const page = parseInt(link.dataset.customPaginationPage || "", 10);

        if (!page) {
          return;
        }

        link.href = buildMainCollectionPaginationUrl(page).toString();
        link.setAttribute("aria-label", buildMainCollectionPaginationLabel(labelTemplate, page));
        link.classList.toggle("pagination__link--disabled", page === currentPage);

        if (page === currentPage) {
          link.setAttribute("aria-current", "page");
        } else {
          link.removeAttribute("aria-current");
        }
      });

    const previousLink = nav.querySelector("[data-custom-pagination-prev]");

    if (previousLink instanceof HTMLAnchorElement) {
      const previousPage = Math.max(1, currentPage - 1);

      previousLink.dataset.customPaginationPage = String(previousPage);
      previousLink.href = buildMainCollectionPaginationUrl(previousPage).toString();
      previousLink.hidden = currentPage <= 1;
      previousLink.setAttribute("aria-label", buildMainCollectionPaginationLabel(labelTemplate, previousPage));
    }

    const nextLink = nav.querySelector("[data-custom-pagination-next]");

    if (nextLink instanceof HTMLAnchorElement) {
      const nextPage = Math.min(totalPages, currentPage + 1);

      nextLink.dataset.customPaginationPage = String(nextPage);
      nextLink.href = buildMainCollectionPaginationUrl(nextPage).toString();
      nextLink.hidden = currentPage >= totalPages;
      nextLink.setAttribute("aria-label", buildMainCollectionPaginationLabel(labelTemplate, nextPage));
    }
  }

  function scrollMainCollectionIntoView(root) {
    const collection = root.closest(".collection");

    if (!(collection instanceof HTMLElement)) {
      return;
    }

    const scrollToCollection = () => {
      collection.scrollIntoView({ block: "start", behavior: "smooth" });
    };

    if ("requestIdleCallback" in window) {
      requestIdleCallback(scrollToCollection, { timeout: 500 });
    } else {
      requestAnimationFrame(scrollToCollection);
    }
  }

  function renderMainCollectionPaginationPage(root, requestedPage, options = {}) {
    if (!(root instanceof HTMLElement)) {
      return;
    }

    applyMainCollectionPaginationNativeLayout(root);

    const visibleList = root.querySelector("[data-custom-pagination-visible-list]");
    const source = root.querySelector("[data-custom-pagination-source]");

    if (!(visibleList instanceof HTMLElement) || !(source instanceof HTMLElement)) {
      return;
    }

    const pageTemplates = Array.from(
      source.querySelectorAll("template[data-custom-pagination-source-page]")
    ).filter((template) => template instanceof HTMLTemplateElement);

    if (pageTemplates.length === 0) {
      return;
    }

    const totalPages = pageTemplates.length;
    const currentPage = Math.min(Math.max(requestedPage || 1, 1), totalPages);
    const pageTemplate =
      source.querySelector(`template[data-custom-pagination-source-page="${currentPage}"]`) ||
      pageTemplates[0];

    if (!(pageTemplate instanceof HTMLTemplateElement)) {
      return;
    }

    visibleList.replaceChildren(pageTemplate.content.cloneNode(true));

    const revealElements = visibleList.querySelectorAll('[reveal-on-scroll="true"]');

    if (revealElements.length > 0) {
      if (typeof visibleList.reveal === "function") {
        visibleList.reveal();
      } else {
        revealElements.forEach((element) => element.removeAttribute("reveal-on-scroll"));
      }
    }

    updateMainCollectionPaginationNav(root, currentPage, totalPages);
    root.dataset.customPaginationCurrentPage = String(currentPage);
    root.dataset.customPaginationRequestedPage = String(requestedPage || 1);

    if (options.updateHistory) {
      const url = buildMainCollectionPaginationUrl(currentPage).toString();

      if (options.historyMode === "push") {
        window.history.pushState({}, "", url);
      } else {
        window.history.replaceState({}, "", url);
      }
    }

    if (options.scroll) {
      scrollMainCollectionIntoView(root);
    }
  }

  function initializeMainCollectionPagination() {
    const requestedPage = getRequestedMainCollectionPaginationPage();

    document.querySelectorAll(MAIN_COLLECTION_PAGINATION_ROOT_SELECTOR).forEach((root) => {
      // `theme:loading:end` also fires for quick-buy loads: a root that already shows the requested page keeps its
      // DOM, otherwise every card is replaced (swatch state, the open modal's parent). A replaced section has no marker.
      if (root.dataset.customPaginationRequestedPage === String(requestedPage)) {
        return;
      }

      renderMainCollectionPaginationPage(root, requestedPage);
    });
  }

  function scheduleMainCollectionPaginationInit() {
    if (scheduledMainCollectionPaginationInit) {
      cancelAnimationFrame(scheduledMainCollectionPaginationInit);
    }

    scheduledMainCollectionPaginationInit = requestAnimationFrame(() => {
      scheduledMainCollectionPaginationInit = 0;
      initializeMainCollectionPagination();
    });
  }

  initializeMainCollectionPagination();
  document.documentElement.addEventListener("theme:loading:end", scheduleMainCollectionPaginationInit);

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest(
        ".shopify-section--main-collection [data-custom-pagination-page]"
      );

      if (!(link instanceof HTMLAnchorElement)) {
        return;
      }

      const root = link.closest("[data-custom-pagination-root]");
      const page = parseInt(link.dataset.customPaginationPage || "", 10);

      if (!(root instanceof HTMLElement) || !page) {
        return;
      }

      event.preventDefault();

      const currentPage = parseInt(root.dataset.customPaginationCurrentPage || "", 10) || 1;

      if (page === currentPage) {
        return;
      }

      renderMainCollectionPaginationPage(root, page, {
        updateHistory: true,
        historyMode: "push",
        scroll: true,
      });
    },
    true
  );

  window.addEventListener("popstate", () => {
    const requestedPage = getRequestedMainCollectionPaginationPage();

    document.querySelectorAll(MAIN_COLLECTION_PAGINATION_ROOT_SELECTOR).forEach((root) => {
      renderMainCollectionPaginationPage(root, requestedPage);
    });
  });

  document.addEventListener(
    "facet:update",
    (event) => {
      const sourceElement = event.target instanceof Element ? event.target : null;
      const paginationTrigger = sourceElement?.closest("[data-custom-pagination-page]");

      if (paginationTrigger) {
        return;
      }

      const url = event.detail?.url;

      if (!(url instanceof URL)) {
        return;
      }

      url.searchParams.delete("mymarini_page");
    },
    true
  );
  // --- CUSTOM SCRIPT END: Main collection pagination controller ---
})();
